import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { usePeriodStore } from '@/stores/period.js';
import { PageHeader, Section } from '@/components/PageHeader.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Input, DateInput, FormField } from '@/components/ui/Input.jsx';
import { Select } from '@/components/ui/Select.jsx';
import { DataTable } from '@/components/DataTable.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/Dialog.jsx';
import { EmployeePicker } from '@/components/EmployeePicker.jsx';
import { Badge } from '@/components/ui/Badge.jsx';
import { useToast } from '@/components/ui/Toast.jsx';
import { formatDate, monthName, formatHours } from '@/lib/format.js';
import { Plus, Trash2, Pencil, Upload, Lock, Wand2 } from 'lucide-react';
import { AttendanceImportDialog } from '@/components/AttendanceImportDialog.jsx';

const STATUS_VARIANT = { present: 'success', absent: 'danger', half_day: 'warning', leave: 'info', holiday: 'brand', weekly_off: 'default' };
const EMPTY_DRAFT = { id: null, employee_id: '', date: new Date().toISOString().slice(0, 10), in_time: '', out_time: '', status: 'present', hours_worked: 0 };

export default function AttendancePage() {
  const { year, month } = usePeriodStore();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);

  const queryKey = ['attendance', { year, month }];
  const { data = [], isLoading } = useQuery({ queryKey, queryFn: () => api.get(`/attendance?year=${year}&month=${month}`) });

  const { data: lockStatus } = useQuery({
    queryKey: ['lock-status', { year, month }],
    queryFn: () => api.lockStatus(year, month),
    staleTime: 30_000,
  });
  const isLocked = lockStatus?.locked ?? false;

  const upsert = useMutation({
    mutationFn: () => api.post('/attendance/upsert', {
      employee_id: Number(draft.employee_id),
      date: draft.date,
      in_time: draft.in_time || null,
      out_time: draft.out_time || null,
      status: draft.status,
      hours_worked: Number(draft.hours_worked || 0),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey }); setOpen(false); toast({ variant: 'success', title: 'Saved' }); },
    onError: (e) => {
      if (e.code === 'PERIOD_LOCKED') toast({ variant: 'error', title: 'Period locked', description: 'This month is finalized and cannot be modified.' });
      else toast({ variant: 'error', title: 'Failed', description: e.message });
    },
  });

  const del = useMutation({
    mutationFn: (id) => api.del(`/attendance/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey }); toast({ variant: 'success', title: 'Deleted' }); },
    onError: (e) => {
      if (e.code === 'PERIOD_LOCKED') toast({ variant: 'error', title: 'Period locked', description: 'This month is finalized and cannot be modified.' });
      else toast({ variant: 'error', title: 'Failed', description: e.message });
    },
  });

  const autoGenerate = useMutation({
    mutationFn: () => api.post('/attendance/auto-generate', { year, month }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey });
      toast({ variant: 'success', title: `Generated ${r.generated} records`, description: `${r.employees} employees × ${r.workingDays} working days` });
    },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });

  function openAdd() {
    setDraft(EMPTY_DRAFT);
    setOpen(true);
  }

  function openEdit(row) {
    setDraft({
      id: row.id,
      employee_id: row.employee_id,
      date: row.date,
      in_time: row.in_time || '',
      out_time: row.out_time || '',
      status: row.status,
      hours_worked: row.hours_worked || 0,
    });
    setOpen(true);
  }

  const columns = [
    { key: 'date', header: 'Date', sortable: true, render: (r) => formatDate(r.date), className: 'font-mono' },
    { key: 'emp_no', header: 'Emp No', sortable: true, className: 'font-mono' },
    { key: 'full_name', header: 'Name', sortable: true },
    { key: 'department_name', header: 'Department' },
    { key: 'in_time', header: 'In', className: 'font-mono', render: (r) => r.in_time ? r.in_time.slice(0, 5) : '' },
    { key: 'out_time', header: 'Out', className: 'font-mono', render: (r) => r.out_time ? r.out_time.slice(0, 5) : '' },
    { key: 'hours_worked', header: 'Hours', className: 'text-right tabular-nums font-mono', headClassName: 'text-right', render: (r) => formatHours(r.hours_worked) },
    { key: 'status', header: 'Status', render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'default'}>{r.status}</Badge> },
    { key: '__a', header: '', className: 'w-20 text-right',
      render: (r) => (
        <div className="flex gap-1 justify-end">
          <Button variant="ghost" size="icon" disabled={isLocked} onClick={() => openEdit(r)}><Pencil className="h-4 w-4 text-slate-500" /></Button>
          <Button variant="ghost" size="icon" disabled={isLocked} onClick={() => del.mutate(r.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      ) },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Attendance"
        description={`${monthName(month)} ${year}${isLocked ? ' — Locked' : ''}`}
        actions={
          <div className="flex gap-2">
            {isLocked && <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400"><Lock className="h-3.5 w-3.5" />Finalized</span>}
            <Button variant="outline" disabled={isLocked || autoGenerate.isPending} onClick={() => autoGenerate.mutate()}><Wand2 className="h-4 w-4" />Auto-Generate</Button>
            <Button variant="outline" disabled={isLocked} onClick={() => setImportOpen(true)}><Upload className="h-4 w-4" />Import</Button>
            <Button disabled={isLocked} onClick={openAdd}><Plus className="h-4 w-4" />Record Attendance</Button>
          </div>
        }
      />
      <Section>
        <DataTable columns={columns} data={data} loading={isLoading} pageSize={20} exportFilename={`attendance-${year}-${month}.csv`} />
      </Section>

      <Dialog open={open} onOpenChange={(v) => { if (!v) setDraft(EMPTY_DRAFT); setOpen(v); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{draft.id ? 'Edit Attendance' : 'Record Attendance'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FormField label="Employee" required>
              <EmployeePicker value={draft.employee_id} onChange={(v) => setDraft({ ...draft, employee_id: v })} disabled={!!draft.id} />
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Date" required><DateInput value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} disabled={!!draft.id} /></FormField>
              <FormField label="Status">
                <Select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
                  {Object.keys(STATUS_VARIANT).map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </FormField>
              <FormField label="In Time"><Input type="time" value={draft.in_time} onChange={(e) => setDraft({ ...draft, in_time: e.target.value })} /></FormField>
              <FormField label="Out Time"><Input type="time" value={draft.out_time} onChange={(e) => setDraft({ ...draft, out_time: e.target.value })} /></FormField>
              <FormField label="Hours Worked" hint="HH:MM — auto-calc from in/out if blank">
                <Input type="text" placeholder="00:00" value={formatHours(draft.hours_worked)}
                  onChange={(e) => {
                    const [hh = '0', mm = '0'] = e.target.value.split(':');
                    setDraft({ ...draft, hours_worked: (parseInt(hh, 10) * 60 + parseInt(mm, 10)) / 60 });
                  }} />
              </FormField>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => upsert.mutate()} disabled={!draft.employee_id || upsert.isPending}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AttendanceImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onSuccess={() => { qc.invalidateQueries({ queryKey }); }}
      />
    </div>
  );
}
